import type { AntiAfk, BehaviourConfig, ScheduleWindow } from '@apiary/shared'

/**
 * Чистая логика редактора поведений: нормализация ввода, валидация и решение,
 * что показывать в форме, когда сервер прислал новое значение.
 *
 * Вынесено из компонента по двум причинам:
 *  - это единственное место, где можно проверить правила окон расписания без
 *    рендера (перевод через полночь, нулевое окно, дни недели);
 *  - те же правила нужны и панели, и будущему мастеру создания бота — вторая
 *    копия разошлась бы с первой при первой же правке.
 *
 * Границы значений сверены со схемой `packages/shared/src/behaviours.ts` и с
 * рантаймом (`apps/runner/src/schedule.ts`, `apps/runner/src/worker/behaviours`):
 * интерфейс не должен разрешать то, что рантайм молча проигнорирует.
 */

/* ------------------------------------------------------------------ анти-AFK */

/**
 * Порядок как в enum схемы — при сохранении список приводится именно к нему,
 * иначе один и тот же набор действий давал бы разный JSON и «Сохранить» мигала
 * бы на пустом месте.
 */
export const ANTI_AFK_ACTIONS: readonly AntiAfk['actions'][number][] = [
  'jump',
  'lookAround',
  'swingArm',
  'sneakToggle',
  'stepAside'
]

/** Подписи без жаргона: пользователь выбирает движения, а не имена методов. */
export const ANTI_AFK_ACTION_LABELS: Record<AntiAfk['actions'][number], string> = {
  jump: 'Подпрыгивать',
  lookAround: 'Осматриваться',
  swingArm: 'Махать рукой',
  sneakToggle: 'Приседать',
  stepAside: 'Делать шаг в сторону'
}

/** Действия, которые работают даже без физики на сервере (docs/04 §6). */
export const ANTI_AFK_ACTIONS_WITHOUT_PHYSICS: readonly AntiAfk['actions'][number][] = [
  'lookAround',
  'swingArm'
]

export const ANTI_AFK_INTERVAL_MIN_SEC = 5
export const ANTI_AFK_INTERVAL_MAX_SEC = 600

/** Включение/выключение одного действия с приведением к каноническому порядку. */
export function toggleAntiAfkAction(
  actions: readonly AntiAfk['actions'][number][],
  action: AntiAfk['actions'][number],
  on: boolean
): AntiAfk['actions'] {
  const next = new Set(actions)
  if (on) next.add(action)
  else next.delete(action)
  return ANTI_AFK_ACTIONS.filter(item => next.has(item))
}

/**
 * Замена одной границы интервала. Отдельная функция, потому что `intervalSec` —
 * кортеж из двух чисел, а литерал массива в обработчике выводится как `number[]`
 * и не подходит по типу.
 */
export function withInterval(
  interval: AntiAfk['intervalSec'],
  edge: 'from' | 'to',
  value: number
): AntiAfk['intervalSec'] {
  return edge === 'from' ? [value, interval[1]] : [interval[0], value]
}

/* -------------------------------------------------------------------- автоеда */

/**
 * Частые «не еда»: то, что бот способен съесть себе во вред или зря потратить.
 * Список — подсказка, а не ограничение: любой другой предмет вводится текстом.
 */
export const KNOWN_BANNED_FOODS: readonly { id: string; label: string }[] = [
  { id: 'rotten_flesh', label: 'Гнилая плоть' },
  { id: 'spider_eye', label: 'Паучий глаз' },
  { id: 'poisonous_potato', label: 'Ядовитый картофель' },
  { id: 'pufferfish', label: 'Рыба-фугу' },
  { id: 'chorus_fruit', label: 'Плод коруса' },
  { id: 'suspicious_stew', label: 'Подозрительный суп' },
  { id: 'chicken', label: 'Сырая курица' },
  { id: 'golden_apple', label: 'Золотое яблоко' },
  { id: 'enchanted_golden_apple', label: 'Зачарованное золотое яблоко' }
]

/**
 * Приведение названия предмета к тому виду, в котором рантайм сравнивает его с
 * инвентарём: `bot.inventory` отдаёт `name` без пространства имён и в нижнем
 * регистре, поэтому «Rotten Flesh» и «minecraft:rotten_flesh» должны сойтись.
 */
export function normalizeFoodId(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^minecraft:/, '')
    .replace(/[\s-]+/g, '_')
}

/** Разделение списка на «известные» (чипы) и «прочие» (текстовое поле). */
export function splitBannedFoods(list: readonly string[]): { known: string[]; extra: string[] } {
  const knownIds = new Set(KNOWN_BANNED_FOODS.map(item => item.id))
  const known: string[] = []
  const extra: string[] = []
  for (const item of list) {
    const id = normalizeFoodId(item)
    if (id === '') continue
    if (knownIds.has(id)) {
      if (!known.includes(id)) known.push(id)
    } else if (!extra.includes(id)) {
      extra.push(id)
    }
  }
  return { known, extra }
}

/** Текст из поля «прочие предметы» → список идентификаторов. */
export function parseFoodInput(text: string): string[] {
  const out: string[] = []
  for (const chunk of text.split(/[,\n;]/)) {
    const id = normalizeFoodId(chunk)
    if (id !== '' && !out.includes(id)) out.push(id)
  }
  return out
}

/* ------------------------------------------------------------------ якорь */

export const MAX_DRIFT_MIN_BLOCKS = 1
export const MAX_DRIFT_MAX_BLOCKS = 256

/* --------------------------------------------------------------- расписание */

/** Дни в порядке недели: понедельник первый, значение — как в `ScheduleWindow.days`. */
export const WEEK_DAYS: readonly { value: number; short: string; full: string }[] = [
  { value: 1, short: 'пн', full: 'понедельник' },
  { value: 2, short: 'вт', full: 'вторник' },
  { value: 3, short: 'ср', full: 'среда' },
  { value: 4, short: 'чт', full: 'четверг' },
  { value: 5, short: 'пт', full: 'пятница' },
  { value: 6, short: 'сб', full: 'суббота' },
  { value: 0, short: 'вс', full: 'воскресенье' }
]

const WEEKDAYS_ONLY = [1, 2, 3, 4, 5]
const WEEKEND_ONLY = [0, 6]

/** Дни в порядке недели (пн…вс), без дублей и мусора. */
export function sortDays(days: readonly number[]): number[] {
  const set = new Set(days.filter(day => Number.isInteger(day) && day >= 0 && day <= 6))
  return WEEK_DAYS.filter(day => set.has(day.value)).map(day => day.value)
}

export function toggleDay(days: readonly number[], day: number, on: boolean): number[] {
  const set = new Set(days)
  if (on) set.add(day)
  else set.delete(day)
  return sortDays([...set])
}

/**
 * Человеческий ввод времени → «ЧЧ:ММ». `null` — распознать не удалось.
 *
 * Принимает «9:5», «0905», «9» и «09:05»: набирать двоеточие и ведущий ноль на
 * каждом окне — лишняя работа, а формат в JSON обязан быть строгим (рантайм
 * разбирает окна регуляркой `^\d{2}:\d{2}$` и молча выключает бота, если время
 * не подошло).
 */
export function normalizeHhMm(raw: string): string | null {
  const compact = raw.trim().replace(/\s+/g, '')
  if (compact === '') return null

  const withColon = /^(\d{1,2}):(\d{1,2})$/.exec(compact)
  const digits = /^(\d{1,4})$/.exec(compact)

  let hours: number
  let minutes: number
  if (withColon) {
    hours = Number(withColon[1])
    minutes = Number(withColon[2])
  } else if (digits) {
    const value = digits[1] ?? ''
    if (value.length <= 2) {
      hours = Number(value)
      minutes = 0
    } else {
      hours = Number(value.slice(0, value.length - 2))
      minutes = Number(value.slice(-2))
    }
  } else {
    return null
  }

  if (hours > 23 || minutes > 59) return null
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/** Новое окно с осмысленными дефолтами: рабочие вечера. */
export function newScheduleWindow(): ScheduleWindow {
  return { days: [...WEEKDAYS_ONLY], from: '18:00', to: '23:00' }
}

/** Окно переходит через полночь — не ошибка, но об этом надо сказать вслух. */
export function crossesMidnight(window: ScheduleWindow): boolean {
  const from = normalizeHhMm(window.from)
  const to = normalizeHhMm(window.to)
  if (from === null || to === null) return false
  return from > to
}

/** Короткая подпись набора дней. */
export function daysLabel(days: readonly number[]): string {
  const sorted = sortDays(days)
  if (sorted.length === 0) return 'дни не выбраны'
  if (sorted.length === 7) return 'ежедневно'
  if (sameSet(sorted, WEEKDAYS_ONLY)) return 'по будням'
  if (sameSet(sorted, WEEKEND_ONLY)) return 'по выходным'
  return sorted.map(day => WEEK_DAYS.find(item => item.value === day)?.short ?? '?').join(', ')
}

/** Подпись окна для сводки: «пн, вт 18:00–23:00». */
export function describeWindow(window: ScheduleWindow): string {
  return `${daysLabel(window.days)} ${window.from}–${window.to}`
}

function sameSet(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every(value => b.includes(value))
}

/**
 * Часовые пояса, которые реально нужны: интерфейс русскоязычный, боты стоят на
 * серверах СНГ и Европы. Полный список IANA (400+ строк) в селекте бесполезен —
 * человек не ищет там «Europe/Kyiv», он ищет «Киев».
 */
export const TIMEZONE_PRESETS: readonly { id: string; label: string }[] = [
  { id: 'Europe/Kyiv', label: 'Киев' },
  { id: 'Europe/Warsaw', label: 'Варшава' },
  { id: 'Europe/Berlin', label: 'Берлин' },
  { id: 'Europe/London', label: 'Лондон' },
  { id: 'Europe/Lisbon', label: 'Лиссабон' },
  { id: 'Europe/Chisinau', label: 'Кишинёв' },
  { id: 'Europe/Minsk', label: 'Минск' },
  { id: 'Europe/Moscow', label: 'Москва' },
  { id: 'Asia/Tbilisi', label: 'Тбилиси' },
  { id: 'Asia/Yerevan', label: 'Ереван' },
  { id: 'Asia/Almaty', label: 'Алматы' },
  { id: 'Asia/Tashkent', label: 'Ташкент' },
  { id: 'UTC', label: 'UTC (всемирное время)' }
]

/**
 * Варианты для селекта пояса: пресеты плюс зона браузера и текущее значение,
 * если их в пресетах нет. Иначе сохранённый кем-то «Asia/Tokyo» пропал бы из
 * списка, а первое же сохранение молча подменило бы его на Киев.
 */
export function timezoneOptions(
  current: string,
  browserZone: string = localZone()
): { value: string; label: string; note: string }[] {
  const ids = new Set(TIMEZONE_PRESETS.map(item => item.id))
  const extra: { id: string; label: string }[] = []
  for (const candidate of [browserZone, current]) {
    if (candidate !== '' && !ids.has(candidate) && isKnownTimezone(candidate)) {
      ids.add(candidate)
      extra.push({ id: candidate, label: cityOf(candidate) })
    }
  }
  return [...extra, ...TIMEZONE_PRESETS].map(item => ({
    value: item.id,
    label: item.label,
    note: item.id
  }))
}

function localZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return ''
  }
}

/** «Asia/Tokyo» → «Tokyo»: подпись без технического разделителя. */
function cityOf(timezone: string): string {
  const tail = timezone.split('/').pop() ?? timezone
  return tail.replace(/_/g, ' ')
}

/* --------------------------------------------------------------- валидация */

export interface WindowProblems {
  days: string | null
  from: string | null
  to: string | null
}

export interface BehaviourProblems {
  intervalFrom: string | null
  intervalTo: string | null
  jitter: string | null
  actions: string | null
  maxDrift: string | null
  startAtFood: string | null
  timezone: string | null
  /** Общая беда расписания: включено, но работать не в чем. */
  schedule: string | null
  /** По индексу окна — те же индексы, что в `schedule.windows`. */
  windows: WindowProblems[]
}

const TIME_HINT = 'время в формате ЧЧ:ММ, например 18:30'

export function validateWindow(window: ScheduleWindow): WindowProblems {
  const from = normalizeHhMm(window.from)
  const to = normalizeHhMm(window.to)
  return {
    days: sortDays(window.days).length === 0 ? 'выберите хотя бы один день' : null,
    from: from === null ? TIME_HINT : null,
    // Нулевое окно рантайм считает «никогда» (`from === to` в windowContains),
    // поэтому ловим его здесь, а не оставляем бота молча выключенным.
    to: to === null ? TIME_HINT : from !== null && from === to ? 'начало и конец совпадают' : null
  }
}

/** Проверка, что часовой пояс известен окружению: иначе рантайм уйдёт в UTC. */
export function isKnownTimezone(timezone: string): boolean {
  if (timezone.trim() === '') return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone })
    return true
  } catch {
    return false
  }
}

export function validateBehaviours(value: BehaviourConfig): BehaviourProblems {
  const [from, to] = value.antiAfk.intervalSec
  const windows = value.schedule.windows.map(validateWindow)

  return {
    intervalFrom:
      from < ANTI_AFK_INTERVAL_MIN_SEC
        ? `не меньше ${ANTI_AFK_INTERVAL_MIN_SEC} с`
        : from > to
          ? 'начало интервала больше конца'
          : null,
    intervalTo: to > ANTI_AFK_INTERVAL_MAX_SEC ? `не больше ${ANTI_AFK_INTERVAL_MAX_SEC} с` : null,
    jitter: value.antiAfk.jitterPct < 0 || value.antiAfk.jitterPct > 100 ? 'от 0 до 100 %' : null,
    // Включённый анти-AFK без действий рантайм считает выключенным
    // (`isEnabled()` в apps/runner/.../anti-afk.ts) — предупреждаем заранее.
    actions:
      value.antiAfk.enabled && value.antiAfk.actions.length === 0
        ? 'выберите хотя бы одно движение, иначе анти-AFK ничего не делает'
        : null,
    maxDrift:
      value.chunkAnchor.maxDriftBlocks < MAX_DRIFT_MIN_BLOCKS ||
      value.chunkAnchor.maxDriftBlocks > MAX_DRIFT_MAX_BLOCKS
        ? `от ${MAX_DRIFT_MIN_BLOCKS} до ${MAX_DRIFT_MAX_BLOCKS} блоков`
        : null,
    startAtFood:
      value.autoEat.startAtFood < 1 || value.autoEat.startAtFood > 19 ? 'от 1 до 19' : null,
    timezone: isKnownTimezone(value.schedule.timezone) ? null : 'выберите часовой пояс из списка',
    schedule:
      value.schedule.enabled && value.schedule.windows.length === 0
        ? 'окон нет — с включённым расписанием бот не запустится вообще'
        : null,
    windows
  }
}

export function hasBehaviourProblems(problems: BehaviourProblems): boolean {
  const flat = [
    problems.intervalFrom,
    problems.intervalTo,
    problems.jitter,
    problems.actions,
    problems.maxDrift,
    problems.startAtFood,
    problems.timezone,
    problems.schedule
  ]
  if (flat.some(item => item !== null)) return true
  return problems.windows.some(w => w.days !== null || w.from !== null || w.to !== null)
}

/* ------------------------------------------------- накопление и сериализация */

/** Сравнение конфигов по значению: черновик «чистый», пока совпадает с сервером. */
export function sameConfig(a: BehaviourConfig, b: BehaviourConfig): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Что показывать в форме, когда сервер прислал новое значение.
 *
 * Правки пользователя важнее свежести: карточка бота инвалидируется по WS на
 * каждую смену состояния, и слепой `setDraft(value)` затирал бы наполовину
 * заполненное окно расписания. Подхватываем сервер только тогда, когда правок
 * не было.
 */
export function mergeServerUpdate(
  draft: BehaviourConfig,
  serverBefore: BehaviourConfig,
  serverAfter: BehaviourConfig
): BehaviourConfig {
  return sameConfig(draft, serverBefore) ? serverAfter : draft
}

/**
 * Приведение черновика к виду, который уходит в `PATCH /bots/:id`.
 *
 * Нормализация здесь, а не по каждому нажатию клавиши: пока человек печатает
 * «18:3», подставлять «18:03» в поле нельзя — курсор прыгает и дописать минуты
 * невозможно.
 */
export function toBehavioursPayload(draft: BehaviourConfig): BehaviourConfig {
  return {
    ...draft,
    antiAfk: {
      ...draft.antiAfk,
      intervalSec: [
        Math.round(draft.antiAfk.intervalSec[0]),
        Math.round(draft.antiAfk.intervalSec[1])
      ],
      actions: toggleAntiAfkActionsOrder(draft.antiAfk.actions),
      jitterPct: Math.round(draft.antiAfk.jitterPct)
    },
    chunkAnchor: {
      ...draft.chunkAnchor,
      maxDriftBlocks: Math.round(draft.chunkAnchor.maxDriftBlocks)
    },
    autoEat: {
      ...draft.autoEat,
      startAtFood: Math.round(draft.autoEat.startAtFood),
      bannedFoods: dedupeFoods(draft.autoEat.bannedFoods)
    },
    schedule: {
      ...draft.schedule,
      windows: draft.schedule.windows.map(window => ({
        days: sortDays(window.days),
        from: normalizeHhMm(window.from) ?? window.from,
        to: normalizeHhMm(window.to) ?? window.to
      }))
    }
  }
}

function toggleAntiAfkActionsOrder(
  actions: readonly AntiAfk['actions'][number][]
): AntiAfk['actions'] {
  return ANTI_AFK_ACTIONS.filter(item => actions.includes(item))
}

function dedupeFoods(list: readonly string[]): string[] {
  const out: string[] = []
  for (const item of list) {
    const id = normalizeFoodId(item)
    if (id !== '' && !out.includes(id)) out.push(id)
  }
  return out
}
